/* DRIVE IQ — BOATING START V2 MAP + MENU AUTHORITY
   File: drive-iq-boating-start.js

   Purpose:
   - Powers the map-first Boating Start V2 shell.
   - Initializes the real Mapbox map.
   - Owns the collapsible planning sheet.
   - Searches destinations and launch points.
   - Displays synchronized map pins and result cards.
   - Tracks destination, launch, boat profile, conditions, and CTA readiness.
   - Sends the normalized boating trip into /pages/boating-drive.

   Optional provider hooks:
   window.DRIVE_IQ_BOATING_SEARCH_DESTINATIONS(context)
   window.DRIVE_IQ_BOATING_SEARCH_LAUNCHES(context)
   window.DRIVE_IQ_BOATING_FETCH_CONDITIONS(context)
   window.DRIVE_IQ_BOATING_PREPARE_ROUTE(context)
*/

(function(window,document){
"use strict";

if(window.DriveIQBoatingStart){
  return;
}

var BUILD="V4_APPROVED_MOCKUP_AUTHORITY";
var STORAGE_KEY="driveiq.boating.start.v2";
var TRIP_STORAGE_KEY="driveiq.boating.trip";
var DEFAULT_DRIVE_URL="/pages/boating-drive";
var MAPBOX_TOKEN="pk.eyJ1Ijoic2VtaWxvY2F0b3IyMDI1IiwiYSI6ImNtanFteTA2eTMyMzIzaG9sODFzbjM4NWgifQ.ulLE-vR8_Q_iNULAK71dbg";
var MAP_STYLE="mapbox://styles/mapbox/navigation-night-v1";
var DEFAULT_CENTER=[-86.4986,30.3859];
var DEFAULT_CENTER_LABEL="Destin Harbor, Florida";

var map=null;
var vesselMarker=null;
var destinationMarker=null;
var launchMarker=null;
var resultMarkers=[];
var selectedResultMarker=null;
var sheetState="expanded";
var locationWatchId=null;

var state={
  ready:false,
  locating:false,
  destination:null,
  launch:null,
  savedLaunch:null,
  returnMode:false,
  exploringNearby:false,
  boatProfile:"pontoon",
  location:null,
  conditions:null,
  destinationType:"",
  routePrepared:false,
  destinationResults:[],
  launchResults:[],
  lastError:"",
  updatedAt:0
};

function byId(id){
  return document.getElementById(id);
}

function clean(value){
  return String(value==null?"":value)
    .replace(/\s+/g," ")
    .trim();
}

function finiteNumber(value){
  var number=Number(value);
  return isFinite(number)?number:null;
}

function escapeHTML(value){
  return String(value==null?"":value)
    .replace(/&/g,"&amp;")
    .replace(/</g,"&lt;")
    .replace(/>/g,"&gt;")
    .replace(/"/g,"&quot;")
    .replace(/'/g,"&#039;");
}

function emit(name,detail){
  try{
    document.dispatchEvent(
      new CustomEvent(name,{detail:detail||{}})
    );
  }catch(error){}
}

function show(element){
  if(element){
    element.classList.remove("biq-hidden");
  }
}

function hide(element){
  if(element){
    element.classList.add("biq-hidden");
  }
}

function setText(id,value){
  var node=byId(id);
  if(node){
    node.textContent=clean(value);
  }
}

function toast(message){
  var node=byId("biqBoatingToast");

  if(!node){
    node=document.createElement("div");
    node.id="biqBoatingToast";
    node.setAttribute("role","status");
    node.style.cssText=[
      "position:fixed",
      "left:50%",
      "bottom:110px",
      "transform:translateX(-50%)",
      "z-index:99999",
      "max-width:min(88vw,420px)",
      "padding:12px 16px",
      "border-radius:999px",
      "background:rgba(4,15,24,.97)",
      "border:1px solid rgba(90,240,210,.25)",
      "color:#fff",
      "box-shadow:0 16px 40px rgba(0,0,0,.35)",
      "font:800 12px/1.25 system-ui,-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif",
      "text-align:center",
      "opacity:0",
      "transition:opacity .2s ease"
    ].join(";");
    document.body.appendChild(node);
  }

  node.textContent=clean(message);
  node.style.opacity="1";

  window.clearTimeout(node.__biqTimer);
  node.__biqTimer=window.setTimeout(function(){
    node.style.opacity="0";
  },2300);
}

function saveState(){
  state.updatedAt=Date.now();

  try{
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify(state)
    );
  }catch(error){}

  return true;
}

function restoreState(){
  var saved;

  try{
    saved=JSON.parse(
      window.localStorage.getItem(STORAGE_KEY)||"null"
    );
  }catch(error){
    saved=null;
  }

  if(!saved||typeof saved!=="object"){
    return false;
  }

  state=Object.assign({},state,saved,{
    ready:false,
    locating:false,
    routePrepared:false,
    returnMode:false,
    exploringNearby:false,
    lastError:"",
    destinationResults:[],
    launchResults:[]
  });

  return true;
}

function normalizePlace(value,fallbackType){
  var source=value&&typeof value==="object"
    ? value
    : {name:clean(value)};
  var center=Array.isArray(source.center)
    ? source.center
    : Array.isArray(source.geometry&&source.geometry.coordinates)
      ? source.geometry.coordinates
      : null;
  var lat=finiteNumber(
    source.lat!=null
      ? source.lat
      : source.latitude!=null
        ? source.latitude
        : center&&center[1]
  );
  var lng=finiteNumber(
    source.lng!=null
      ? source.lng
      : source.longitude!=null
        ? source.longitude
        : center&&center[0]
  );

  return {
    id:clean(
      source.id||
      source.placeId||
      source.place_id||
      ""
    ),
    name:clean(
      source.name||
      source.label||
      source.title||
      source.text||
      source.place_name||
      source.formatted||
      ""
    ),
    address:clean(
      source.address||
      source.formattedAddress||
      source.place_name||
      source.full||
      ""
    ),
    city:clean(
      source.city||
      source.town||
      source.village||
      ""
    ),
    state:clean(source.state||""),
    lat:lat,
    lng:lng,
    type:clean(
      source.type||
      source.category||
      fallbackType||
      ""
    ),
    source:clean(source.source||"drive-iq-boating")
  };
}

function radians(value){
  return Number(value)*Math.PI/180;
}

function distanceMeters(a,b){
  if(!a||!b){
    return null;
  }

  var firstLat=finiteNumber(a.lat);
  var firstLng=finiteNumber(a.lng);
  var secondLat=finiteNumber(b.lat);
  var secondLng=finiteNumber(b.lng);

  if(
    firstLat===null||
    firstLng===null||
    secondLat===null||
    secondLng===null
  ){
    return null;
  }

  var radius=6371000;
  var dLat=radians(secondLat-firstLat);
  var dLng=radians(secondLng-firstLng);
  var h=
    Math.sin(dLat/2)*Math.sin(dLat/2)+
    Math.cos(radians(firstLat))*
    Math.cos(radians(secondLat))*
    Math.sin(dLng/2)*
    Math.sin(dLng/2);

  return radius*2*Math.atan2(
    Math.sqrt(h),
    Math.sqrt(1-h)
  );
}

function validPlaceCoordinates(place){
  return !!(
    place&&
    finiteNumber(place.lat)!==null&&
    finiteNumber(place.lng)!==null&&
    Math.abs(Number(place.lat))<=90&&
    Math.abs(Number(place.lng))<=180
  );
}

function placeLngLat(place){
  return validPlaceCoordinates(place)
    ? [Number(place.lng),Number(place.lat)]
    : null;
}

function callAdapter(name,context){
  var adapter=window[name];

  if(typeof adapter!=="function"){
    return Promise.resolve(null);
  }

  try{
    return Promise.resolve(adapter(context));
  }catch(error){
    return Promise.reject(error);
  }
}

function mapboxQuery(query,options){
  var supplied=options||{};
  var proximity=supplied.proximity;
  var types=supplied.types||"poi,place,locality,neighborhood,address";
  var limit=Math.max(1,Math.min(10,Number(supplied.limit||8)));
  var url=
    "https://api.mapbox.com/geocoding/v5/mapbox.places/"+
    encodeURIComponent(query)+
    ".json?access_token="+
    encodeURIComponent(MAPBOX_TOKEN)+
    "&autocomplete=true"+
    "&limit="+limit+
    "&types="+encodeURIComponent(types);

  if(
    proximity&&
    isFinite(Number(proximity.lng))&&
    isFinite(Number(proximity.lat))
  ){
    url+="&proximity="+
      encodeURIComponent(
        Number(proximity.lng)+","+
        Number(proximity.lat)
      );
  }

  return fetch(url,{
    method:"GET",
    credentials:"omit"
  })
    .then(function(response){
      if(!response.ok){
        throw new Error("Mapbox search failed");
      }
      return response.json();
    })
    .then(function(payload){
      return Array.isArray(payload&&payload.features)
        ? payload.features.map(function(feature){
            return normalizePlace(
              Object.assign({},feature,{
                source:"mapbox-geocoding"
              }),
              supplied.fallbackType||"marine_destination"
            );
          })
        : [];
    });
}

function destinationQueryText(context){
  var query=clean(context.query);
  var type=clean(context.destinationType);

  if(query){
    return query;
  }

  if(state.exploringNearby&&type){
    if(type==="boat_ramp"){return "boat ramp";}
    if(type==="marina"){return "marina";}
    if(type==="fuel_dock"){return "marine fuel";}
    if(type==="restaurant"){return "waterfront restaurant";}
    if(type==="fishing"){return "fishing access";}
    if(type==="beach"){return "beach cove";}
    if(type==="hangout"){return "sandbar party cove boating hangout raft up";}
  }

  if(type==="boat_ramp"){
    return "boat ramp";
  }
  if(type==="marina"){
    return "marina";
  }
  if(type==="fuel_dock"){
    return "marine fuel dock";
  }
  if(type==="restaurant"){
    return "waterfront restaurant";
  }
  if(type==="fishing"){
    return "fishing access";
  }
  if(type==="hangout"){
    return "sandbar party cove boating hangout raft up";
  }

  return "marina";
}

function launchQueryText(context){
  var query=clean(context.query);

  if(query){
    return query;
  }

  if(
    context.destination&&
    context.destination.name
  ){
    return "boat ramp marina near "+
      context.destination.name;
  }

  return "boat ramp marina";
}

function markerIconForType(type){
  var key=clean(type).toLowerCase();

  if(key.indexOf("ramp")>=0){return "↘";}
  if(key.indexOf("fuel")>=0){return "⛽";}
  if(key.indexOf("restaurant")>=0){return "🍽";}
  if(key.indexOf("fishing")>=0){return "🎣";}
  if(key.indexOf("marina")>=0){return "⚓";}
  if(key.indexOf("hangout")>=0||key.indexOf("sandbar")>=0||key.indexOf("cove")>=0){return "☀";}

  return "◆";
}

function markerElement(place,selected){
  var element=document.createElement("button");

  element.type="button";
  element.className="biq-map-pin";
  element.setAttribute(
    "data-selected",
    selected===true?"true":"false"
  );
  element.setAttribute(
    "aria-label",
    clean(place&&place.name||"Marine place")
  );
  element.textContent=markerIconForType(
    place&&place.type
  );

  return element;
}

function removeResultMarkers(){
  resultMarkers.forEach(function(entry){
    try{
      entry.marker.remove();
    }catch(error){}
  });

  resultMarkers=[];
  selectedResultMarker=null;
}

function setMapStatus(message,visible){
  setText("biqStartMapStatus",message);

  if(visible===false){
    hide(byId("biqStartMapStatus"));
  }else{
    show(byId("biqStartMapStatus"));
  }
}

function mapStyle(){
  return clean(
    window.DRIVE_IQ_BOATING_MAP_STYLE||
    "mapbox://styles/mapbox/navigation-night-v1"
  );
}

function initializeMap(){
  if(!window.mapboxgl){
    state.lastError="Mapbox GL JS unavailable";
    setMapStatus("Mapbox failed to load",true);
    return Promise.resolve(false);
  }

  mapboxgl.accessToken=MAPBOX_TOKEN;

  var initialCenter=
    state.location&&
    isFinite(Number(state.location.lng))&&
    isFinite(Number(state.location.lat))
      ? [Number(state.location.lng),Number(state.location.lat)]
      : DEFAULT_CENTER;

  map=new mapboxgl.Map({
    container:"biqStartMap",
    style:mapStyle(),
    center:initialCenter,
    zoom:12.8,
    pitch:42,
    bearing:0,
    attributionControl:false,
    antialias:true
  });

  map.addControl(
    new mapboxgl.NavigationControl({
      showCompass:true,
      showZoom:false,
      visualizePitch:true
    }),
    "bottom-right"
  );

  return new Promise(function(resolve){
    map.once("load",function(){
      setMapStatus("",false);
      updateMapState(true);
      emit("boating:start-map-ready",{build:BUILD});
      resolve(true);
    });

    map.on("error",function(event){
      state.lastError=clean(
        event&&event.error&&event.error.message||
        "Mapbox error"
      );
      emit("boating:start-map-error",{
        message:state.lastError
      });
    });
  });
}

function fitMapToPlaces(places,options){
  var supplied=options||{};
  var valid=(places||[]).filter(validPlaceCoordinates);

  if(!map||!valid.length){
    return false;
  }

  if(valid.length===1){
    map.easeTo({
      center:placeLngLat(valid[0]),
      zoom:Number(supplied.zoom||13.2),
      pitch:46,
      duration:700
    });
    return true;
  }

  var bounds=new mapboxgl.LngLatBounds();

  valid.forEach(function(place){
    bounds.extend(placeLngLat(place));
  });

  map.fitBounds(bounds,{
    padding:{
      top:150,
      right:50,
      bottom:300,
      left:50
    },
    maxZoom:14,
    duration:800
  });

  return true;
}

function updatePermanentMarkers(){
  if(!map){
    return false;
  }

  var location=state.location;
  var destination=state.destination;
  var launch=state.savedLaunch||state.launch;

  if(
    location&&
    isFinite(Number(location.lng))&&
    isFinite(Number(location.lat))
  ){
    if(!vesselMarker){
      var vessel=document.createElement("div");
      vessel.className="biq-map-pin";
      vessel.setAttribute("data-selected","true");
      vessel.textContent="●";

      vesselMarker=new mapboxgl.Marker({
        element:vessel,
        anchor:"center"
      });
    }

    vesselMarker
      .setLngLat([
        Number(location.lng),
        Number(location.lat)
      ])
      .addTo(map);
  }

  if(validPlaceCoordinates(destination)){
    if(!destinationMarker){
      destinationMarker=new mapboxgl.Marker({
        element:markerElement(destination,true),
        anchor:"bottom"
      });
    }

    destinationMarker
      .setLngLat(placeLngLat(destination))
      .addTo(map);
  }else if(destinationMarker){
    destinationMarker.remove();
    destinationMarker=null;
  }

  if(validPlaceCoordinates(launch)){
    if(!launchMarker){
      launchMarker=new mapboxgl.Marker({
        element:markerElement(
          Object.assign({},launch,{type:"boat_ramp"}),
          true
        ),
        anchor:"bottom"
      });
    }

    launchMarker
      .setLngLat(placeLngLat(launch))
      .addTo(map);
  }else if(launchMarker){
    launchMarker.remove();
    launchMarker=null;
  }

  return true;
}

function paintResultMarkers(items,mode){
  if(!map){
    return false;
  }

  removeResultMarkers();

  (items||[])
    .filter(validPlaceCoordinates)
    .forEach(function(place){
      var element=markerElement(place,false);
      var marker=new mapboxgl.Marker({
        element:element,
        anchor:"bottom"
      })
        .setLngLat(placeLngLat(place))
        .addTo(map);

      element.addEventListener("click",function(){
        if(mode==="launch"){
          selectLaunch(place);
        }else{
          selectDestination(place);
        }
      });

      resultMarkers.push({
        marker:marker,
        element:element,
        place:place
      });
    });

  fitMapToPlaces(items);
  return true;
}

function updateMapState(fit){
  if(!map){
    return false;
  }

  updatePermanentMarkers();

  var places=[];

  if(validPlaceCoordinates(state.location)){
    places.push(state.location);
  }
  if(validPlaceCoordinates(state.destination)){
    places.push(state.destination);
  }
  if(validPlaceCoordinates(state.launch)){
    places.push(state.launch);
  }

  if(fit&&places.length){
    fitMapToPlaces(places,{
      zoom:places.length===1?13:undefined
    });
  }

  return true;
}

function setSheetState(nextState){
  var sheet=byId("biqPlanningSheet");
  var toggle=byId("biqSheetToggle");
  var allowed=["expanded","peek","collapsed"];

  if(allowed.indexOf(nextState)<0){
    nextState="expanded";
  }

  sheetState=nextState;

  if(sheet){
    sheet.setAttribute("data-state",sheetState);
  }

  if(toggle){
    toggle.textContent=
      sheetState==="expanded"
        ? "Collapse"
        : "Expand";
  }

  emit("boating:sheet-state-changed",{
    state:sheetState
  });

  return sheetState;
}

function cycleSheet(){
  if(sheetState==="expanded"){
    return setSheetState("peek");
  }
  if(sheetState==="peek"){
    return setSheetState("collapsed");
  }
  return setSheetState("expanded");
}

function openSheet(){
  return setSheetState("expanded");
}

function savedLaunchDistanceText(){
  if(
    !state.savedLaunch||
    !state.location
  ){
    return "Home base ready";
  }

  var meters=distanceMeters(
    state.location,
    state.savedLaunch
  );

  if(meters===null){
    return "Home base ready";
  }

  var miles=meters/1609.344;

  if(miles<0.1){
    return Math.max(
      50,
      Math.round(miles*5280/50)*50
    )+" ft away";
  }

  return (
    miles<10
      ? miles.toFixed(1)
      : Math.round(miles)
  )+" mi away";
}

function markMyLaunch(){
  if(!state.location){
    toast("Location is still being resolved");
    return false;
  }

  state.savedLaunch={
    id:"saved-launch-"+Date.now(),
    name:"My Launch / Parked Here",
    address:"Saved from current location",
    lat:Number(state.location.lat),
    lng:Number(state.location.lng),
    type:"saved_launch",
    source:"drive-iq-mark-launch",
    capturedAt:Date.now()
  };

  state.launch=state.savedLaunch;
  state.returnMode=false;
  saveState();
  render();
  updateMapState(false);

  toast("Home Base saved");
  emit("boating:launch-home-marked",{
    launch:state.savedLaunch
  });

  return true;
}

function returnToLaunch(){
  if(!state.savedLaunch){
    toast("Mark your launch first");
    return false;
  }

  state.returnMode=true;
  state.destination=state.savedLaunch;
  saveState();
  render();
  updateMapState(true);

  toast("Return marker active");
  emit("boating:return-to-launch",{
    launch:state.savedLaunch,
    location:state.location
  });

  return true;
}

function exploreAroundMe(type){
  var planningCenter=
    validPlaceCoordinates(state.destination)
      ? state.destination
      : state.location;

  if(!planningCenter){
    return locate().then(function(){
      return exploreAroundMe(type);
    });
  }

  state.exploringNearby=true;
  state.destinationType=clean(type||state.destinationType||"marina");
  saveState();
  renderQuickDestination();
  openSheet();

  if(byId("biqDestinationInput")){
    byId("biqDestinationInput").value="";
  }

  return searchDestinations();
}

function shareMyLocation(){
  if(!state.location){
    toast("Location is not ready yet");
    return Promise.resolve(false);
  }

  var text=
    "My current boating location: "+
    Number(state.location.lat).toFixed(6)+","+
    Number(state.location.lng).toFixed(6);

  if(state.savedLaunch){
    text+=" | Saved launch: "+
      Number(state.savedLaunch.lat).toFixed(6)+","+
      Number(state.savedLaunch.lng).toFixed(6);
  }

  if(
    navigator.share&&
    typeof navigator.share==="function"
  ){
    return navigator.share({
      title:"Drive IQ Marine Location",
      text:text,
      url:"https://maps.google.com/?q="+
        encodeURIComponent(
          state.location.lat+","+
          state.location.lng
        )
    })
      .then(function(){return true;})
      .catch(function(){return false;});
  }

  try{
    navigator.clipboard.writeText(text);
    toast("Location copied");
    return Promise.resolve(true);
  }catch(error){
    toast(text);
    return Promise.resolve(false);
  }
}

function openAssistance(){
  emit("boating:assistance-requested",{
    location:state.location,
    savedLaunch:state.savedLaunch
  });

  toast("Coordinates are ready to share");
  return shareMyLocation();
}

function renderBoatProfile(){
  Array.prototype.forEach.call(
    document.querySelectorAll(
      "[data-biq-boat-profile]"
    ),
    function(button){
      button.setAttribute(
        "data-active",
        button.getAttribute(
          "data-biq-boat-profile"
        )===state.boatProfile
          ? "true"
          : "false"
      );
    }
  );
}

function renderQuickDestination(){
  Array.prototype.forEach.call(
    document.querySelectorAll(
      "[data-biq-destination-type]"
    ),
    function(button){
      button.setAttribute(
        "data-active",
        button.getAttribute(
          "data-biq-destination-type"
        )===state.destinationType
          ? "true"
          : "false"
      );
    }
  );
}

function renderStatus(){
  setText(
    "biqGpsStatus",
    state.location
      ? "Location ready"
      : state.locating
        ? "Locating vessel"
        : "Location unavailable"
  );

  setText(
    "biqWeatherStatus",
    state.conditions
      ? "Conditions ready"
      : "Checking conditions"
  );

  setText(
    "biqRouteStatus",
    state.routePrepared
      ? "Awareness ready"
      : state.destination
        ? clean(state.destination.name||"Water selected")
        : state.location
          ? "No plan set"
          : "Waiting for location"
  );
}

function renderConditions(){
  var conditions=state.conditions||{};

  setText(
    "biqWindValue",
    conditions.windMph!=null
      ? Math.round(Number(conditions.windMph))+" mph"
      : "-- mph"
  );

  setText(
    "biqWindNote",
    conditions.windNote||
    (
      state.location
        ? "No wind advisory"
        : "Awaiting location"
    )
  );

  setText(
    "biqWeatherValue",
    conditions.temperatureF!=null
      ? Math.round(Number(conditions.temperatureF))+"°"
      : "--°"
  );

  setText(
    "biqWeatherNote",
    conditions.weatherNote||
    conditions.summary||
    "Checking"
  );

  setText(
    "biqWaterValue",
    conditions.waterStatus||
    conditions.waterLevel||
    "Unknown"
  );

  setText(
    "biqWaterNote",
    conditions.waterNote||
    "Pending"
  );

  setText(
    "biqConfidenceValue",
    conditions.routeConfidence||
    (
      state.destination&&state.launch
        ? "Ready"
        : "Not ready"
    )
  );

  setText(
    "biqConfidenceNote",
    state.savedLaunch
      ? savedLaunchDistanceText()
      : "Mark your launch"
  );

  var windAlert=byId("biqWindAlert");
  var stormAlert=byId("biqStormAlert");
  var visibilityAlert=byId("biqVisibilityAlert");
  var waterAlert=byId("biqWaterAlert");

  if(windAlert){
    windAlert.textContent=
      conditions.windMph!=null
        ? "Wind • "+Math.round(Number(conditions.windMph))+" mph"
        : "Wind • Checking";
    windAlert.setAttribute(
      "data-state",
      conditions.windMph!=null&&Number(conditions.windMph)>=18
        ? "warning"
        : conditions.windMph!=null
          ? "good"
          : ""
    );
  }

  if(stormAlert){
    stormAlert.textContent=
      conditions.stormRisk
        ? "Storms • Watch"
        : "Storms • Clear";
    stormAlert.setAttribute(
      "data-state",
      conditions.stormRisk
        ? "warning"
        : "good"
    );
  }

  if(visibilityAlert){
    visibilityAlert.textContent=
      "Visibility • "+
      clean(conditions.visibilityStatus||"Checking");
    visibilityAlert.setAttribute(
      "data-state",
      conditions.visibilityWarning
        ? "warning"
        : conditions.visibilityStatus
          ? "good"
          : ""
    );
  }

  if(waterAlert){
    waterAlert.textContent=
      "Water • "+
      clean(
        conditions.waterStatus||
        conditions.waterLevel||
        "Checking"
      );
    waterAlert.setAttribute(
      "data-state",
      conditions.waterWarning
        ? "warning"
        : conditions.waterStatus
          ? "good"
          : ""
    );
  }
}

function render(){
  var destinationInput=byId("biqDestinationInput");
  var launchInput=byId("biqLaunchInput");

  if(
    destinationInput&&
    state.destination&&
    state.destination.name&&
    document.activeElement!==destinationInput
  ){
    destinationInput.value=state.destination.name;
  }

  if(
    launchInput&&
    state.launch&&
    state.launch.name&&
    document.activeElement!==launchInput
  ){
    launchInput.value=state.launch.name;
  }

  renderBoatProfile();
  renderQuickDestination();
  renderStatus();
  renderConditions();

  var savedCard=byId("biqSavedLaunchCard");
  if(state.savedLaunch){
    show(savedCard);
    setText(
      "biqSavedLaunchName",
      state.savedLaunch.name||
      "My Launch / Parked Here"
    );
    setText(
      "biqSavedLaunchDistance",
      savedLaunchDistanceText()
    );
    setText(
      "biqConfidenceValue",
      state.returnMode
        ? "Returning"
        : "Marked"
    );
    setText(
      "biqConfidenceNote",
      savedLaunchDistanceText()
    );
  }else{
    hide(savedCard);
    setText("biqConfidenceValue","Not marked");
    setText("biqConfidenceNote","Mark your launch");
  }

  updateMapState(false);
}

function resultList(containerId,items,onSelect,emptyText){
  var container=byId(containerId);
  var list=Array.isArray(items)?items:[];

  if(!container){
    return;
  }

  if(!list.length){
    container.innerHTML=
      "<div style='padding:12px;border:1px solid rgba(255,255,255,.08);border-radius:14px;color:#aebdc7;background:rgba(255,255,255,.035);font-size:11px;'>"+
      escapeHTML(emptyText||"No matches found")+
      "</div>";
    show(container);
    return;
  }

  container.innerHTML="";

  list.slice(0,8).forEach(function(item){
    var place=normalizePlace(item);
    var button=document.createElement("button");

    button.type="button";
    button.style.cssText=[
      "display:block",
      "width:100%",
      "margin-top:9px",
      "padding:15px 15px",
      "border:1px solid rgba(105,240,201,.14)",
      "border-radius:18px",
      "background:linear-gradient(180deg,rgba(255,255,255,.05),rgba(255,255,255,.025))",
      "color:#fff",
      "box-shadow:0 10px 24px rgba(0,0,0,.16)",
      "text-align:left",
      "cursor:pointer"
    ].join(";");

    button.innerHTML=
      "<strong style='display:block;font-size:14px;line-height:1.2;'>"+
      escapeHTML(place.name||"Unnamed place")+
      "</strong>"+
      "<span style='display:block;margin-top:6px;color:#9fb0bc;font-size:10px;line-height:1.35;'>"+
      escapeHTML(
        place.address||
        place.city||
        place.type||
        "Marine destination"
      )+
      "</span>";

    button.addEventListener("click",function(){
      onSelect(place);
      hide(container);
    });

    container.appendChild(button);
  });

  show(container);
}

function looksLikeExplicitDestinationQuery(query){
  var value=clean(query).toLowerCase();

  return !!(
    value.length>=4&&
    (
      /\b(lake|river|bay|harbor|harbour|reservoir|sound|inlet|beach|island|marina|boat ramp|landing)\b/.test(value)||
      /,/.test(value)
    )
  );
}

function normalizedSearchText(value){
  return clean(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g," ")
    .replace(/\s+/g," ")
    .trim();
}

function resultRelevanceScore(place,query){
  var q=normalizedSearchText(query);
  var name=normalizedSearchText(place&&place.name);
  var address=normalizedSearchText(place&&place.address);
  var full=(name+" "+address).trim();
  var score=0;

  if(!q){
    return 0;
  }

  if(name===q){
    score+=1000;
  }else if(name.indexOf(q)===0){
    score+=700;
  }else if(name.indexOf(q)>=0){
    score+=500;
  }

  q.split(" ").forEach(function(token){
    if(token.length<3){
      return;
    }

    if(name.indexOf(token)>=0){
      score+=120;
    }else if(full.indexOf(token)>=0){
      score+=45;
    }
  });

  if(/\blake\b/.test(q)&&/\blake\b/.test(full)){
    score+=90;
  }

  if(/\bmarina\b/.test(q)&&/\bmarina\b/.test(full)){
    score+=90;
  }

  return score;
}

function rankDestinationResults(items,query){
  return (items||[])
    .slice()
    .sort(function(a,b){
      return resultRelevanceScore(b,query)-
        resultRelevanceScore(a,query);
    });
}

function destinationSearchContext(){
  return {
    query:clean(
      byId("biqDestinationInput")&&
      byId("biqDestinationInput").value
    ),
    destinationType:state.destinationType,
    location:state.location,
    planningCenter:
      validPlaceCoordinates(state.destination)
        ? state.destination
        : state.location,
    boatProfile:state.boatProfile,
    limit:8
  };
}

function searchDestinations(){
  var context=destinationSearchContext();

  if(clean(context.query)){
    state.exploringNearby=false;
  }

  var query=destinationQueryText(context);
  var button=byId("biqFindDestination");

  if(!query){
    toast("Enter a lake, marina, ramp, or destination");
    return Promise.resolve([]);
  }

  openSheet();

  if(button){
    button.disabled=true;
    button.textContent="FINDING";
  }

  emit("boating:destination-search-started",context);

  return callAdapter(
    "DRIVE_IQ_BOATING_SEARCH_DESTINATIONS",
    context
  )
    .then(function(response){
      var providerItems=
        Array.isArray(response)
          ? response
          : response&&Array.isArray(response.results)
            ? response.results
            : [];

      /*
        Search authority:
        An installed provider hook may exist but return an empty result set.
        Empty provider output must not suppress the working Mapbox search.
      */
      if(providerItems.length){
        return providerItems;
      }

      return mapboxQuery(query,{
        proximity:
          state.exploringNearby
            ? (
                validPlaceCoordinates(state.destination)
                  ? state.destination
                  : state.location
              )
            : looksLikeExplicitDestinationQuery(query)
              ? null
              : state.location,
        limit:8,
        fallbackType:
          state.destinationType||
          "marine_destination"
      });
    })
    .then(function(items){
      if(
        (!items||!items.length)&&
        clean(query).length>=2
      ){
        return mapboxQuery(
          query+" lake marina",
          {
            proximity:null,
            limit:8,
            fallbackType:
              state.destinationType||
              "marine_destination"
          }
        );
      }

      return items||[];
    })
    .then(function(items){
      state.destinationResults=rankDestinationResults(
        (items||[])
          .map(function(item){
            return normalizePlace(
              item,
              state.destinationType
            );
          }),
        query
      );

      if(
        looksLikeExplicitDestinationQuery(query)&&
        (
          !state.destinationResults.length||
          resultRelevanceScore(
            state.destinationResults[0],
            query
          )<120
        )
      ){
        return mapboxQuery(
          query,
          {
            proximity:null,
            limit:10,
            fallbackType:
              state.destinationType||
              "marine_destination"
          }
        ).then(function(globalItems){
          state.destinationResults=rankDestinationResults(
            (globalItems||[]).map(function(item){
              return normalizePlace(
                item,
                state.destinationType
              );
            }),
            query
          );

          return state.destinationResults;
        });
      }

      return state.destinationResults;
    })
    .then(function(){
      resultList(
        "biqDestinationResults",
        state.destinationResults,
        selectDestination,
        "No boating destinations found"
      );

      paintResultMarkers(
        state.destinationResults,
        "destination"
      );

      saveState();

      emit("boating:destination-search-completed",{
        context:context,
        count:state.destinationResults.length
      });

      window.setTimeout(function(){
        var resultsNode=byId("biqDestinationResults");
        var sheetScroll=byId("biqSheetScroll");

        try{
          if(resultsNode&&sheetScroll){
            sheetScroll.scrollTo({
              top:Math.max(
                0,
                resultsNode.offsetTop-20
              ),
              behavior:"smooth"
            });
          }
        }catch(error){}
      },60);

      return state.destinationResults;
    })
    .catch(function(error){
      state.lastError=clean(
        error&&error.message||error
      );
      toast("Destination search could not complete");
      return [];
    })
    .then(function(items){
      if(button){
        button.disabled=false;
        button.textContent="FIND";
      }
      return items;
    });
}

function selectDestination(value){
  state.destination=normalizePlace(
    value,
    state.destinationType
  );

  state.launch=null;
  state.routePrepared=false;
  removeResultMarkers();

  if(byId("biqDestinationInput")){
    byId("biqDestinationInput").value=
      state.destination.name;
  }

  if(byId("biqLaunchInput")){
    byId("biqLaunchInput").value="";
  }

  saveState();
  render();
  updateMapState(true);
  setSheetState("peek");
  fetchConditions();

  toast((state.destination.name||"Destination")+" selected");
  emit("boating:destination-selected",{
    destination:state.destination
  });

  return state.destination;
}

function launchSearchContext(){
  return {
    query:clean(
      byId("biqLaunchInput")&&
      byId("biqLaunchInput").value
    ),
    destination:state.destination,
    location:state.location,
    boatProfile:state.boatProfile,
    nearest:true,
    limit:8
  };
}

function searchLaunches(){
  var context=launchSearchContext();
  var query=launchQueryText(context);
  var button=byId("biqUseNearestLaunch");
  var proximity=
    validPlaceCoordinates(state.destination)
      ? state.destination
      : state.location;

  openSheet();

  if(button){
    button.disabled=true;
    button.textContent="FINDING";
  }

  emit("boating:launch-search-started",context);

  return callAdapter(
    "DRIVE_IQ_BOATING_SEARCH_LAUNCHES",
    context
  )
    .then(function(response){
      var providerItems=
        Array.isArray(response)
          ? response
          : response&&Array.isArray(response.results)
            ? response.results
            : [];

      if(providerItems.length){
        return providerItems;
      }

      return mapboxQuery(query,{
        proximity:proximity,
        limit:8,
        fallbackType:"boat_ramp"
      });
    })
    .then(function(items){
      state.launchResults=(items||[])
        .map(function(item){
          return normalizePlace(item,"boat_ramp");
        });

      resultList(
        "biqLaunchResults",
        state.launchResults,
        selectLaunch,
        "No launch points found"
      );

      paintResultMarkers(
        state.launchResults,
        "launch"
      );

      saveState();
      return state.launchResults;
    })
    .catch(function(error){
      state.lastError=clean(
        error&&error.message||error
      );
      toast("Launch search could not complete");
      return [];
    })
    .then(function(items){
      if(button){
        button.disabled=false;
        button.textContent="NEAREST";
      }
      return items;
    });
}

function selectLaunch(value){
  state.launch=normalizePlace(value,"boat_ramp");
  state.routePrepared=false;
  removeResultMarkers();

  if(byId("biqLaunchInput")){
    byId("biqLaunchInput").value=
      state.launch.name;
  }

  saveState();
  render();
  updateMapState(true);
  fetchConditions();

  toast("Launch point selected");
  emit("boating:launch-selected",{
    launch:state.launch
  });

  return state.launch;
}

function locate(){
  if(
    !window.navigator||
    !window.navigator.geolocation
  ){
    state.locating=false;
    render();
    return Promise.resolve(null);
  }

  state.locating=true;
  render();

  return new Promise(function(resolve){
    window.navigator.geolocation.getCurrentPosition(
      function(position){
        state.locating=false;
        state.location={
          lat:Number(position.coords.latitude),
          lng:Number(position.coords.longitude),
          accuracyMeters:Number(
            position.coords.accuracy||0
          ),
          capturedAt:Date.now()
        };

        saveState();
        render();
        updateMapState(true);
        fetchConditions();

        emit("boating:location-ready",{
          location:state.location
        });

        resolve(state.location);
      },
      function(error){
        state.locating=false;
        state.lastError=clean(
          error&&error.message||
          "Location unavailable"
        );
        render();

        emit("boating:location-failed",{
          message:state.lastError
        });

        resolve(null);
      },
      {
        enableHighAccuracy:true,
        timeout:9000,
        maximumAge:30000
      }
    );
  });
}

function startLocationWatch(){
  if(
    !navigator.geolocation||
    locationWatchId!==null
  ){
    return false;
  }

  locationWatchId=navigator.geolocation.watchPosition(
    function(position){
      state.location={
        lat:Number(position.coords.latitude),
        lng:Number(position.coords.longitude),
        accuracyMeters:Number(
          position.coords.accuracy||0
        ),
        capturedAt:Number(
          position.timestamp||Date.now()
        )
      };

      saveState();
      render();
      updateMapState(false);
    },
    function(){},
    {
      enableHighAccuracy:true,
      timeout:10000,
      maximumAge:5000
    }
  );

  return true;
}

function fetchConditions(){
  var context={
    location:state.location,
    destination:state.destination,
    launch:state.launch,
    boatProfile:state.boatProfile
  };

  if(
    !context.location&&
    !context.destination&&
    !context.launch
  ){
    return Promise.resolve(null);
  }

  return callAdapter(
    "DRIVE_IQ_BOATING_FETCH_CONDITIONS",
    context
  )
    .then(function(response){
      if(
        response&&
        typeof response==="object"&&
        !Array.isArray(response)
      ){
        state.conditions=response;
        saveState();
        render();

        emit("boating:conditions-ready",{
          conditions:state.conditions
        });
      }

      return state.conditions;
    })
    .catch(function(error){
      state.lastError=clean(
        error&&error.message||error
      );
      render();
      return null;
    });
}

function tripPayload(){
  return {
    version:2,
    createdAt:Date.now(),
    destination:state.destination,
    launch:state.launch,
    savedLaunch:state.savedLaunch,
    returnMode:state.returnMode,
    awarenessMode:true,
    boatProfile:state.boatProfile,
    location:state.location,
    conditions:state.conditions,
    routeSource:"boating-start-v4-approved-mockup"
  };
}

function validateTrip(payload){
  if(
    !validPlaceCoordinates(payload.location)&&
    !validPlaceCoordinates(payload.destination)
  ){
    return "Use your location or choose a lake first";
  }

  if(
    payload.savedLaunch&&
    !validPlaceCoordinates(payload.savedLaunch)
  ){
    return "Saved launch location is invalid";
  }

  return "";
}

function openBoatingDrive(payload){
  var encoded;

  try{
    window.sessionStorage.setItem(
      TRIP_STORAGE_KEY,
      JSON.stringify(payload)
    );
  }catch(error){}

  encoded=encodeURIComponent(
    window.btoa(
      unescape(
        encodeURIComponent(
          JSON.stringify(payload)
        )
      )
    )
  );

  window.location.href=
    DEFAULT_DRIVE_URL+
    "?trip="+encoded;
}

function prepareTrip(){
  var payload=tripPayload();
  var validation=validateTrip(payload);
  var button=byId("biqPrepareTrip");

  if(validation){
    toast(validation);
    openSheet();
    return Promise.resolve(false);
  }

  if(button){
    button.disabled=true;
    button.firstChild.nodeValue="STARTING AWARENESS ";
  }

  emit("boating:route-prepare-started",{
    trip:payload
  });

  return callAdapter(
    "DRIVE_IQ_BOATING_PREPARE_ROUTE",
    payload
  )
    .then(function(response){
      var routePayload=
        response&&typeof response==="object"
          ? Object.assign({},payload,response)
          : payload;

      state.routePrepared=true;
      saveState();
      render();

      emit("boating:route-prepared",{
        trip:routePayload
      });

      openBoatingDrive(routePayload);
      return true;
    })
    .catch(function(error){
      state.lastError=clean(
        error&&error.message||error
      );
      toast("Boating awareness could not start");
      return false;
    })
    .then(function(result){
      if(button){
        button.disabled=false;
        button.firstChild.nodeValue=
          "START BOATING AWARENESS ";
      }
      return result;
    });
}

function goBack(){
  try{
    if(window.history.length>1){
      window.history.back();
      return;
    }
  }catch(error){}

  window.location.href="/";
}

function bind(){
  var destinationInput=byId("biqDestinationInput");
  var launchInput=byId("biqLaunchInput");

  if(byId("biqBoatingBack")){
    byId("biqBoatingBack")
      .addEventListener("click",goBack);
  }
  if(byId("biqUseMyLocation")){
    byId("biqUseMyLocation")
      .addEventListener("click",function(){
        locate().then(function(location){
          if(location){
            state.destination=null;
            state.destinationType="";
            state.exploringNearby=false;
            state.routePrepared=false;

            if(byId("biqDestinationInput")){
              byId("biqDestinationInput").value="";
            }

            hide(byId("biqDestinationResults"));
            removeResultMarkers();
            saveState();
            render();
            updateMapState(true);
            setSheetState("peek");
            toast("Starting from your current location");
          }
        });
      });
  }

  if(byId("biqMarkLaunch")){
    byId("biqMarkLaunch")
      .addEventListener("click",markMyLaunch);
  }

  if(byId("biqExploreAroundMe")){
    byId("biqExploreAroundMe")
      .addEventListener("click",function(){
        exploreAroundMe("marina");
      });
  }

  if(byId("biqReturnToLaunch")){
    byId("biqReturnToLaunch")
      .addEventListener("click",returnToLaunch);
  }

  if(byId("biqShareLocation")){
    byId("biqShareLocation")
      .addEventListener("click",shareMyLocation);
  }

  if(byId("biqAssistance")){
    byId("biqAssistance")
      .addEventListener("click",openAssistance);
  }


  if(byId("biqSheetGrab")){
    byId("biqSheetGrab")
      .addEventListener("click",cycleSheet);
  }

  if(byId("biqSheetToggle")){
    byId("biqSheetToggle")
      .addEventListener("click",cycleSheet);
  }

  if(byId("biqFindDestination")){
    byId("biqFindDestination")
      .addEventListener(
        "click",
        searchDestinations
      );
  }

  if(destinationInput){
    destinationInput.addEventListener(
      "focus",
      openSheet
    );

    destinationInput.addEventListener(
      "keydown",
      function(event){
        if(event.key==="Enter"){
          event.preventDefault();
          searchDestinations();
        }
      }
    );

    destinationInput.addEventListener(
      "input",
      function(){
        state.destination=null;
        state.launch=null;
        state.routePrepared=false;
        saveState();
        renderStatus();
      }
    );
  }

  Array.prototype.forEach.call(
    document.querySelectorAll(
      "[data-biq-destination-type]"
    ),
    function(button){
      button.addEventListener(
        "click",
        function(){
          state.destinationType=
            button.getAttribute(
              "data-biq-destination-type"
            )||"";

          saveState();
          renderQuickDestination();
          exploreAroundMe(state.destinationType);
        }
      );
    }
  );

  Array.prototype.forEach.call(
    document.querySelectorAll(
      "[data-biq-boat-profile]"
    ),
    function(button){
      button.addEventListener(
        "click",
        function(){
          state.boatProfile=
            button.getAttribute(
              "data-biq-boat-profile"
            )||"pontoon";

          saveState();
          renderBoatProfile();
          fetchConditions();

          emit("boating:profile-selected",{
            boatProfile:state.boatProfile
          });
        }
      );
    }
  );

  if(byId("biqUseNearestLaunch")){
    byId("biqUseNearestLaunch")
      .addEventListener(
        "click",
        searchLaunches
      );
  }

  if(launchInput){
    launchInput.addEventListener(
      "focus",
      openSheet
    );

    launchInput.addEventListener(
      "keydown",
      function(event){
        if(event.key==="Enter"){
          event.preventDefault();
          searchLaunches();
        }
      }
    );

    launchInput.addEventListener(
      "input",
      function(){
        state.launch=null;
        state.routePrepared=false;
        saveState();
        renderStatus();
      }
    );
  }

  if(byId("biqPrepareTrip")){
    byId("biqPrepareTrip")
      .addEventListener(
        "click",
        prepareTrip
      );
  }

  if(byId("biqBoatingSettings")){
    byId("biqBoatingSettings")
      .addEventListener(
        "click",
        function(){
          cycleSheet();
        }
      );
  }

  window.addEventListener(
    "resize",
    function(){
      if(map){
        map.resize();
      }
    }
  );
}

function status(){
  return JSON.parse(JSON.stringify({
    build:BUILD,
    ready:state.ready,
    destination:state.destination,
    launch:state.launch,
    boatProfile:state.boatProfile,
    location:state.location,
    conditions:state.conditions,
    routePrepared:state.routePrepared,
    planningCenter:
      validPlaceCoordinates(state.destination)
        ? state.destination
        : state.location,
    uiPhase:
      state.routePrepared
        ? "ready"
        : state.destination
          ? "water-selected"
          : state.location
            ? "location-ready"
            : "locating",
    sheetState:sheetState,
    lastError:state.lastError
  }));
}

function reset(){
  state={
    ready:true,
    locating:false,
    destination:null,
    launch:null,
    savedLaunch:null,
    returnMode:false,
    exploringNearby:false,
    boatProfile:"pontoon",
    location:null,
    conditions:null,
    destinationType:"",
    routePrepared:false,
    destinationResults:[],
    launchResults:[],
    lastError:"",
    updatedAt:Date.now()
  };

  try{
    window.localStorage.removeItem(
      STORAGE_KEY
    );
  }catch(error){}

  if(byId("biqDestinationInput")){
    byId("biqDestinationInput").value="";
  }

  if(byId("biqLaunchInput")){
    byId("biqLaunchInput").value="";
  }

  hide(byId("biqDestinationResults"));
  hide(byId("biqLaunchResults"));
  removeResultMarkers();
  setSheetState("collapsed");
  render();

  emit("boating:start-reset",{});
  return true;
}

function init(){
  if(state.ready){
    return true;
  }

  restoreState();
  bind();
  setSheetState("expanded");

  state.ready=true;
  saveState();
  render();

  initializeMap()
    .then(function(){
      return locate();
    })
    .then(function(){
      startLocationWatch();
      return fetchConditions();
    })
    .catch(function(error){
      state.lastError=clean(
        error&&error.message||error
      );
    });

  emit("boating:start-ready",{
    build:BUILD
  });

  return true;
}



/* =========================================================
   DRIVE IQ BOATING START V3.1.1
   CANONICAL WATER SEARCH — PRIMARY SCOPE AUTHORITY

   This code is intentionally installed inside the original Boating Start
   private scope so it can safely own search, ranking, map pins, selection,
   and trip handoff without ReferenceError failures.
========================================================= */

var V311_WATER_REGISTRY=[
  {
    id:"water-pickwick-lake",
    name:"Pickwick Lake",
    address:"Tennessee / Mississippi / Alabama",
    city:"Pickwick Dam",
    state:"Tennessee",
    lat:35.0495,
    lng:-88.2517,
    type:"lake",
    source:"drive-iq-canonical-water",
    canonical:true,
    aliases:["pickwick lake","lake pickwick","pickwick reservoir","pickwick"]
  },
  {
    id:"water-sardis-lake-ms",
    name:"Sardis Lake",
    address:"Sardis, Mississippi",
    city:"Sardis",
    state:"Mississippi",
    lat:34.4307,
    lng:-89.7957,
    type:"lake",
    source:"drive-iq-canonical-water",
    canonical:true,
    aliases:["sardis lake","lake sardis","sardis reservoir"]
  },
  {
    id:"water-arkabutla-lake",
    name:"Arkabutla Lake",
    address:"Arkabutla, Mississippi",
    city:"Arkabutla",
    state:"Mississippi",
    lat:34.7448,
    lng:-90.1234,
    type:"lake",
    source:"drive-iq-canonical-water",
    canonical:true,
    aliases:["arkabutla lake","lake arkabutla","arkabutla reservoir"]
  },
  {
    id:"water-grenada-lake",
    name:"Grenada Lake",
    address:"Grenada, Mississippi",
    city:"Grenada",
    state:"Mississippi",
    lat:33.8426,
    lng:-89.7858,
    type:"lake",
    source:"drive-iq-canonical-water",
    canonical:true,
    aliases:["grenada lake","lake grenada","grenada reservoir"]
  },
  {
    id:"water-enid-lake",
    name:"Enid Lake",
    address:"Enid, Mississippi",
    city:"Enid",
    state:"Mississippi",
    lat:34.1593,
    lng:-89.8773,
    type:"lake",
    source:"drive-iq-canonical-water",
    canonical:true,
    aliases:["enid lake","lake enid","enid reservoir"]
  },
  {
    id:"water-destin-harbor",
    name:"Destin Harbor",
    address:"Destin, Florida",
    city:"Destin",
    state:"Florida",
    lat:30.3939,
    lng:-86.5087,
    type:"harbor",
    source:"drive-iq-canonical-water",
    canonical:true,
    aliases:["destin harbor","destin harbour","destin pass","east pass destin"]
  }
];

function v311Norm(value){
  return clean(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g," ")
    .replace(/\s+/g," ")
    .trim();
}

function v311CanonicalMatches(query){
  var q=v311Norm(query);
  var out=[];
  var i,j,item,alias;

  if(!q){return out;}

  for(i=0;i<V311_WATER_REGISTRY.length;i++){
    item=V311_WATER_REGISTRY[i];

    for(j=0;j<item.aliases.length;j++){
      alias=v311Norm(item.aliases[j]);

      if(q===alias||alias.indexOf(q)===0||q.indexOf(alias)>=0){
        out.push(normalizePlace(item,item.type));
        break;
      }
    }
  }

  return out;
}

function v311IsWaterIntent(query){
  return /\b(lake|reservoir|river|bay|harbor|harbour|sound|inlet|beach|island|cove|marina|boat ramp|landing)\b/i.test(clean(query));
}

function v311StreetFalseMatch(place,query){
  var name=v311Norm(place&&place.name);
  var full=v311Norm(
    String(place&&place.name||"")+" "+
    String(place&&place.address||"")
  );

  if(!v311IsWaterIntent(query)){return false;}

  if(/\b(street|st|road|rd|drive|dr|lane|ln|avenue|ave|boulevard|blvd|court|ct|way|trail|trl|highway|hwy)\b/.test(name)){
    return true;
  }

  if(
    /\b(lane|drive|road|street|avenue|boulevard|court|way)\b/.test(full)&&
    !/\b(lake|reservoir|river|bay|harbor|harbour|sound|inlet|beach|island|cove|marina|boat ramp|landing)\b/.test(name)
  ){
    return true;
  }

  return false;
}

function v311ForeignFalseMatch(place){
  var full=v311Norm(
    String(place&&place.address||"")+" "+
    String(place&&place.city||"")+" "+
    String(place&&place.state||"")
  );

  return /\b(canada|united kingdom|england|scotland|france|spain|morocco|iceland|alberta|ontario)\b/.test(full);
}

function v311Rank(items,query){
  var q=v311Norm(query);
  var combined=v311CanonicalMatches(query).concat(items||[]);
  var seen={};
  var out=[];

  combined.forEach(function(raw){
    var place=normalizePlace(raw,state.destinationType||"marine_destination");
    var lat=finiteNumber(place.lat);
    var lng=finiteNumber(place.lng);
    var key;
    var score;

    if(lat===null||lng===null){return;}

    key=[
      v311Norm(place.name),
      lat.toFixed(4),
      lng.toFixed(4)
    ].join("|");

    if(seen[key]){return;}
    seen[key]=true;

    if(v311StreetFalseMatch(place,q)){return;}
    if(v311ForeignFalseMatch(place)){return;}

    score=resultRelevanceScore(place,query);

    if(place.source==="drive-iq-canonical-water"){score+=10000;}
    if(place.canonical===true){score+=10000;}
    if(v311Norm(place.name)===q){score+=4000;}

    place._v311Score=score;
    out.push(place);
  });

  out.sort(function(a,b){
    return Number(b._v311Score||0)-Number(a._v311Score||0);
  });

  return out.slice(0,8);
}

var v311OriginalRankDestinationResults=rankDestinationResults;
rankDestinationResults=function(items,query){
  if(v311IsWaterIntent(query)||v311CanonicalMatches(query).length){
    return v311Rank(items,query);
  }

  return v311OriginalRankDestinationResults(items,query);
};

var v311OriginalSearchDestinations=searchDestinations;
searchDestinations=function(){
  var input=byId("biqDestinationInput");
  var query=clean(input&&input.value);
  var canonical=v311CanonicalMatches(query);

  if(!canonical.length){
    return v311OriginalSearchDestinations().then(function(items){
      if(v311IsWaterIntent(query)){
        state.destinationResults=v311Rank(items||[],query);

        resultList(
          "biqDestinationResults",
          state.destinationResults,
          selectDestination,
          "No boating destinations found"
        );

        paintResultMarkers(state.destinationResults,"destination");
        saveState();
      }

      return state.destinationResults;
    });
  }

  state.exploringNearby=false;
  state.destinationResults=v311Rank(canonical,query);

  resultList(
    "biqDestinationResults",
    state.destinationResults,
    selectDestination,
    "No boating destinations found"
  );

  paintResultMarkers(state.destinationResults,"destination");
  openSheet();
  saveState();

  emit("boating:destination-search-completed",{
    context:destinationSearchContext(),
    count:state.destinationResults.length,
    source:"canonical-water-registry"
  });

  return Promise.resolve(state.destinationResults);
};

var v311OriginalSelectDestination=selectDestination;
selectDestination=function(value){
  var selected=normalizePlace(value,state.destinationType);

  if(value&&value.canonical===true){
    selected.canonical=true;
  }

  if(selected.source==="drive-iq-canonical-water"){
    selected.canonical=true;
  }

  selected.waterEntity=!!(
    selected.canonical||
    v311IsWaterIntent(selected.type+" "+selected.name)
  );

  selected.center={
    lat:selected.lat,
    lng:selected.lng
  };

  return v311OriginalSelectDestination(selected);
};

var v311OriginalTripPayload=tripPayload;
tripPayload=function(){
  var payload=v311OriginalTripPayload();
  var center=null;

  if(validPlaceCoordinates(payload.destination)){
    center={
      lat:Number(payload.destination.lat),
      lng:Number(payload.destination.lng),
      label:payload.destination.name||"Boating destination",
      source:"selected-water-destination"
    };
  }else if(validPlaceCoordinates(payload.location)){
    center={
      lat:Number(payload.location.lat),
      lng:Number(payload.location.lng),
      label:"Current location",
      source:"current-location"
    };
  }

  payload.version=4;
  payload.routeSource="boating-start-v4-approved-mockup";
  payload.selectedWaterCenter=center;
  payload.center=center;
  payload.startupContext={
    mode:payload.destination?"planned-destination":"current-location",
    selectedWaterCenter:center,
    hasConfirmedLaunch:!!(
      (payload.launch||payload.savedLaunch)&&
      validPlaceCoordinates(payload.launch||payload.savedLaunch)
    ),
    hasLiveLocation:!!(
      payload.location&&validPlaceCoordinates(payload.location)
    ),
    onWaterConfirmed:false,
    title:payload.destination
      ? "Boating awareness ready"
      : "Marine awareness ready",
    summary:payload.destination
      ? "Centered on "+payload.destination.name+". Mark your launch when you arrive."
      : payload.savedLaunch
        ? "Home Base saved. Marine awareness is ready."
        : "Live position ready. Mark your launch before heading out."
  };

  return payload;
};

var v311OriginalOpenBoatingDrive=openBoatingDrive;
openBoatingDrive=function(payload){
  try{
    sessionStorage.setItem(
      "driveiq.boating.selected-water-center",
      JSON.stringify(payload&&payload.selectedWaterCenter||null)
    );
    sessionStorage.setItem(
      "driveiq.boating.startup-context",
      JSON.stringify(payload&&payload.startupContext||null)
    );
  }catch(error){}

  return v311OriginalOpenBoatingDrive(payload);
};




/* =========================================================
   DRIVE IQ BOATING START V3.1.2
   CANONICAL AUTO-SELECT + CTA HANDOFF AUTHORITY

   Fixes:
   - A single canonical water result is automatically selected.
   - Start Boating Awareness cannot silently fall back to current location
     while a canonical lake search result is on screen.
   - The CTA uses the selected canonical destination even when the user
     does not tap the result card first.
========================================================= */

function v312CanonicalResultOnScreen(){
  var input=byId("biqDestinationInput");
  var query=clean(input&&input.value);
  var matches=v311CanonicalMatches(query);

  if(matches.length===1){
    return normalizePlace(matches[0],matches[0].type||"lake");
  }

  return null;
}

function v312EnsureCanonicalDestination(){
  var canonical=v312CanonicalResultOnScreen();

  if(!canonical){
    return state.destination;
  }

  if(
    !state.destination||
    state.destination.source!=="drive-iq-canonical-water"||
    state.destination.id!==canonical.id
  ){
    state.destination=canonical;
    state.destination.canonical=true;
    state.destination.waterEntity=true;
    state.destination.center={
      lat:state.destination.lat,
      lng:state.destination.lng
    };

    state.launch=null;
    state.routePrepared=false;

    if(byId("biqDestinationInput")){
      byId("biqDestinationInput").value=state.destination.name;
    }

    saveState();
    render();
    updateMapState(true);

    emit("boating:destination-auto-selected",{
      destination:state.destination,
      source:"canonical-single-result"
    });
  }

  return state.destination;
}

var v312OriginalSearchDestinations=searchDestinations;
searchDestinations=function(){
  return v312OriginalSearchDestinations().then(function(items){
    var canonical=v312CanonicalResultOnScreen();

    if(canonical){
      v312EnsureCanonicalDestination();
      toast((canonical.name||"Water destination")+" selected");
    }

    return items;
  });
};

var v312OriginalPrepareTrip=prepareTrip;
prepareTrip=function(){
  v312EnsureCanonicalDestination();

  if(
    !state.destination&&
    v312CanonicalResultOnScreen()
  ){
    toast("Select the lake before continuing");
    openSheet();
    return Promise.resolve(false);
  }

  return v312OriginalPrepareTrip();
};

var v312OriginalTripPayload=tripPayload;
tripPayload=function(){
  v312EnsureCanonicalDestination();
  return v312OriginalTripPayload();
};


window.DriveIQBoatingStart={
  init:init,
  reset:reset,
  status:status,
  state:function(){
    return state;
  },
  map:function(){
    return map;
  },
  locate:locate,
  searchDestinations:searchDestinations,
  selectDestination:selectDestination,
  searchLaunches:searchLaunches,
  selectLaunch:selectLaunch,
  markMyLaunch:markMyLaunch,
  returnToLaunch:returnToLaunch,
  exploreAroundMe:exploreAroundMe,
  shareMyLocation:shareMyLocation,
  fetchConditions:fetchConditions,
  prepareTrip:prepareTrip,
  tripPayload:tripPayload,
  setSheetState:setSheetState,
  ensureCanonicalDestination:v312EnsureCanonicalDestination,
  canonicalWaterSearch:v311CanonicalMatches,
  waterRegistry:function(){return V311_WATER_REGISTRY.slice();},
  build:BUILD
};

if(document.readyState==="loading"){
  document.addEventListener(
    "DOMContentLoaded",
    init,
    {once:true}
  );
}else{
  init();
}

})(window,document);
